import { useMemo } from 'react';

import { hasValue } from 'utils/hasValue';

import {
  useGetFxCurrencies,
  useGetFxMarkups,
  type GetFxMarkups200DataItem
} from 'api/customerApi';

import { useOrganisationId } from 'hooks/context';

import { adaptMarkupsToBeBidirectional } from './adapters';
import { getBuysOptions, getPaysOptions } from './generateCurrencyOptions';
import { useFormatCurrencyOptionLabel } from './useFormatCurrencyOptionLabel';

export function useCurrencyOptions(
  currentPayCurrency?: string,
  currentBuyCurrency?: string
) {
  const organisationId = useOrganisationId<number>();

  const { data: markupData, isPending: isLoadingMarkups } =
    useGetFxMarkups(organisationId);

  const { data: currencies, isPending: isLoadingCurrencies } =
    useGetFxCurrencies(organisationId);

  const formatCurrencyOptionLabel = useFormatCurrencyOptionLabel();

  const enhancedMarkupData = useMemo(() => {
    if (hasValue(markupData) && hasValue(markupData.data)) {
      return adaptMarkupsToBeBidirectional(markupData.data);
    }
  }, [markupData]);

  const allCurrenciesAsOptions = useMemo(() => {
    return currencies?.data?.map((currency) => ({
      value: currency.iso_code,
      label: currency.iso_code
    }));
  }, [currencies?.data]);

  const payOptions = useMemo(() => {
    return enhancedMarkupData
      ? generateUniqueOptions(enhancedMarkupData, 'pay_currency_iso')
      : [];
  }, [enhancedMarkupData]);

  const buyOptions = useMemo(() => {
    return enhancedMarkupData
      ? generateUniqueOptions(enhancedMarkupData, 'buy_currency_iso')
      : [];
  }, [enhancedMarkupData]);

  const pays = useMemo(() => {
    if (hasValue(enhancedMarkupData) && hasValue(allCurrenciesAsOptions)) {
      return getPaysOptions({
        tradedCurrency: currentBuyCurrency,
        markupData: enhancedMarkupData,
        payOptions,
        currencies: allCurrenciesAsOptions
      })?.map(formatCurrencyOptionLabel);
    }
  }, [
    allCurrenciesAsOptions,
    currentBuyCurrency,
    enhancedMarkupData,
    formatCurrencyOptionLabel,
    payOptions
  ]);

  const buys = useMemo(() => {
    if (hasValue(enhancedMarkupData) && hasValue(allCurrenciesAsOptions)) {
      return getBuysOptions({
        tradedCurrency: currentPayCurrency,
        markupData: enhancedMarkupData,
        buyOptions,
        currencies: allCurrenciesAsOptions
      })?.map(formatCurrencyOptionLabel);
    }
  }, [
    allCurrenciesAsOptions,
    buyOptions,
    currentPayCurrency,
    enhancedMarkupData,
    formatCurrencyOptionLabel
  ]);

  return {
    pays,
    buys,
    isLoading: isLoadingCurrencies || isLoadingMarkups
  };
}

const generateUniqueOptions = (
  data: GetFxMarkups200DataItem[],
  key: 'pay_currency_iso' | 'buy_currency_iso'
) => {
  const seenCurrencies = new Set();
  return data
    .filter((markup) => {
      const currency = markup[key];
      if (seenCurrencies.has(currency)) {
        return false;
      }
      seenCurrencies.add(currency);
      return true;
    })
    .map((markup) => ({
      id: markup.id,
      value: markup[key],
      label: markup[key]
    }));
};
